/** The light frame listing, frame detail on demand, archive and HTML poster upload. */
import { apiRequest } from "@/api/client";
import type { Frame, FrameStatus, FrameType } from "@/api/frame-model";

/**
 * One row of the paginated frame summary listing (`GET …/frames/?summary=1`).
 * Everything a card needs without `content`: the persisted `card` stands in
 * for it and `metadata` carries only the keys a listing reads.
 */
export interface FrameSummary {
  id: string;
  /** The frame's project; `null` only when the frame lost it. */
  project_id: string | null;
  frame_type: FrameType;
  title: string;
  status: FrameStatus;
  card: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  agent_id: string | null;
  parent_frame: string | null;
  task_id: string | null;
  task_ticket_number: number | null;
  task_title: string | null;
  created_at: string;
  updated_at: string;
  edited_by_user_at: string | null;
  archived_at: string | null;
}

/**
 * Which rows a summary page lists (`?view=`): `all` is the canvas list
 * without archived frames; `wall` is the wall page and `tools` its Pinned row
 * (one page); `archived` the archived frames. Mirrors `VIEWS` in
 * `backend/apps/spaces/wall_listing.py`.
 */
export type FrameListingView = "all" | "wall" | "tools" | "archived";

export interface FrameSummaryQuery {
  /** `nextCursor` of the previous page; omitted for the first page. */
  cursor?: string;
  /** 1–100; the server default is 40. */
  limit?: number;
  frameType?: FrameType;
  /** Full-text search: one ranked page without a cursor. */
  q?: string;
  /** The server default is `all`. */
  view?: FrameListingView;
  includeHidden?: boolean;
  /** `all` only: archived frames too. */
  includeArchived?: boolean;
  signal?: AbortSignal;
}

export interface FrameSummaryPage {
  items: FrameSummary[];
  hasMore: boolean;
  nextCursor: string | null;
}

interface FrameSummaryResponse {
  status: "success";
  data: FrameSummary[];
  pagination: { has_more: boolean; next_cursor: string | null };
}

interface FrameResponse {
  status: "success";
  data: Frame;
}

/**
 * One page of a project's frame summaries, newest first. Silent: the caller
 * reports failures (an aborted page is not one).
 */
export async function listFrameSummaries(
  projectId: string,
  {
    cursor,
    limit,
    frameType,
    q,
    view,
    includeHidden,
    includeArchived,
    signal,
  }: FrameSummaryQuery = {},
): Promise<FrameSummaryPage> {
  const params = new URLSearchParams({ summary: "1" });
  if (cursor) params.set("cursor", cursor);
  if (limit !== undefined) params.set("limit", String(limit));
  if (frameType) params.set("frame_type", frameType);
  if (q) params.set("q", q);
  if (view) params.set("view", view);
  if (includeHidden) params.set("include_hidden", "true");
  if (includeArchived) params.set("include_archived", "true");
  const res = await apiRequest<FrameSummaryResponse>(
    `/api/v1/projects/${projectId}/frames/?${params}`,
    { silent: true, signal },
  );
  return summaryPage(res);
}

export interface WallSummaryQuery {
  /** The project whose Pinned row shows beside the feed; its pins stay out of the feed. */
  currentProjectId?: string;
  /** `nextCursor` of the previous page; omitted for the first page. */
  cursor?: string;
  /** 1–100; the server default is 40. */
  limit?: number;
  /** Full-text search over the feed and that Pinned row: one ranked page without a cursor. */
  q?: string;
  signal?: AbortSignal;
}

/**
 * One page of the wall across the user's active projects, newest first
 * (`GET /api/v1/frames/wall/`). No project's board or memory is listed;
 * other projects' pins are plain cards. Silent, like `listFrameSummaries`.
 */
export async function listWallSummariesAcrossProjects({
  currentProjectId,
  cursor,
  limit,
  q,
  signal,
}: WallSummaryQuery = {}): Promise<FrameSummaryPage> {
  const params = new URLSearchParams();
  if (currentProjectId) params.set("current_project_id", currentProjectId);
  if (cursor) params.set("cursor", cursor);
  if (limit !== undefined) params.set("limit", String(limit));
  if (q) params.set("q", q);
  const query = params.toString();
  const res = await apiRequest<FrameSummaryResponse>(
    `/api/v1/frames/wall/${query ? `?${query}` : ""}`,
    { silent: true, signal },
  );
  return summaryPage(res);
}

function summaryPage(res: FrameSummaryResponse): FrameSummaryPage {
  return {
    items: res.data,
    hasMore: res.pagination.has_more,
    nextCursor: res.pagination.next_cursor,
  };
}

const FRAME_ID_PATTERN = /^[\w-]+$/;

/**
 * True for a plain frame id. Ids from links and content are checked before
 * they are fetched: one is put in a request path.
 */
export function isFrameId(id: string): boolean {
  return FRAME_ID_PATTERN.test(id);
}

/** One frame with its full content. Silent: the caller reports failures. */
export async function getFrame(
  projectId: string,
  frameId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Frame> {
  const res = await apiRequest<FrameResponse>(
    `/api/v1/projects/${projectId}/frames/${encodeURIComponent(frameId)}/`,
    { silent: true, signal },
  );
  return res.data;
}

/**
 * One frame of any of the user's active projects, with full content
 * (`GET /api/v1/frames/<id>/`): a card Denker presents from its own project.
 * Silent: the caller reports failures.
 */
export async function getUserFrame(frameId: string): Promise<Frame> {
  const res = await apiRequest<FrameResponse>(
    `/api/v1/frames/${encodeURIComponent(frameId)}/`,
    { silent: true },
  );
  return res.data;
}

async function setFrameArchived(
  projectId: string,
  frameId: string,
  method: "POST" | "DELETE",
): Promise<Frame> {
  const res = await apiRequest<FrameResponse>(
    `/api/v1/projects/${projectId}/frames/${encodeURIComponent(frameId)}/archive/`,
    { method, silent: true },
  );
  return res.data;
}

/**
 * Take a frame off the wall and out of search; its
 * `updated_at` stays. Silent: the caller reverts and reports a failure.
 */
export function archiveFrame(
  projectId: string,
  frameId: string,
): Promise<Frame> {
  return setFrameArchived(projectId, frameId, "POST");
}

/** Bring an archived frame back. Silent, like `archiveFrame`. */
export function unarchiveFrame(
  projectId: string,
  frameId: string,
): Promise<Frame> {
  return setFrameArchived(projectId, frameId, "DELETE");
}

export type FrameThumbnailTheme = "light" | "dark";

export interface FrameThumbnailUpload {
  /** An `ImageUpload` the user owns (JPEG or PNG). */
  imageId: string;
  theme: FrameThumbnailTheme;
  /**
   * Lowercase hex SHA-256 of the UTF-8 bytes of `content.html` exactly as
   * the capture rendered it (`""` when absent). The server answers 409 when
   * the stored page no longer matches.
   */
  contentSha256: string;
  /** What the card shows, stored beside the poster (`HtmlCover`, a PDF's pages). */
  cover?: FrameThumbnailCover;
}

/** The cover descriptor as the server stores it; nulls are left out. */
export interface FrameThumbnailCover {
  kind: "cover" | "block" | "pdf";
  headline?: string;
  line?: string;
  tint?: string | null;
  placeholder?: string | null;
  /** A PDF's page count. */
  pages?: number;
}

function coverBody(cover: FrameThumbnailCover): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(cover).filter(([, value]) => value !== null && value !== ""),
  );
}

/**
 * Store a captured poster for an HTML frame's card in one theme, with what
 * the card shows. Silent: a poster is background work and a failed one
 * leaves the colour block.
 */
export async function putFrameThumbnail(
  projectId: string,
  frameId: string,
  { imageId, theme, contentSha256, cover }: FrameThumbnailUpload,
): Promise<Frame> {
  const res = await apiRequest<FrameResponse>(
    `/api/v1/projects/${projectId}/frames/${encodeURIComponent(frameId)}/thumbnail/`,
    {
      method: "PUT",
      body: {
        image_id: imageId,
        theme,
        content_sha256: contentSha256,
        ...(cover ? { cover: coverBody(cover) } : {}),
      },
      silent: true,
    },
  );
  return res.data;
}
